import * as React from "react";
import { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import {
  defaultStrokeColor,
  mapActive,
  mapUtils,
} from "../../../map/utils/map-utils";
import { Feature, FeatureCollection, Geometry } from "geojson";
import { Box } from "@chakra-ui/react";
import { GeoJsonNodeProps } from "../../../osaway/models/CustomGeoJsonProperties";
import { OsaNode } from "../../../osaway/models/OsaNode";
import { LifeCycleStage } from "../../../osaway/models/LifeCycleStage";
import { useStores } from "../../../shared/stores/RootStore";
import { OsaWay } from "../../../osaway/models/OsaWay";

const MainMapBase: React.FC<{
  isHome?: boolean;
  reducedMapSize?: boolean;
  onMapReady?: (map: mapboxgl.Map) => void;
}> = ({ reducedMapSize = false, isHome = false, onMapReady }) => {
  const {
    mapStore,
    filterStore,
    projectStore,
    osaWayStore,
    osaCommentStore,
    osaNodeStore,
    hlaStore,
  } = useStores();
  const map = mapStore.map;
  const selectedOsaWay = osaWayStore.getSelected();
  const selectedNode = osaNodeStore.getSelected();
  const selectedProject = projectStore.getSelected();
  const selectedHlaWay = hlaStore.getSelected();

  const [processedGeoJson, processedPlannedTrajectory] = useMemo<
    FeatureCollection[]
  >(() => {
    const geoJsonFeatures: Feature[] = [];
    const plannedTrajectoryFeatures: Feature[] = [];

    const isFeatureVisible = (node: OsaNode) => {
      return (
        filterStore.isNodeVisibleByAttributes(node.osaWayId, node.id) &&
        filterStore.isNodeVisibleByScore(node.score) &&
        filterStore.isNodeVisibleByTreatment(node.treatments ?? null)
      );
    };

    const getColor = (node: OsaNode) => {
      return isFeatureVisible(node)
        ? mapStore.applyColorBySettings(node)
        : defaultStrokeColor;
    };

    const getShowCommentIcon = (feature: Feature): boolean => {
      const props = feature.properties as GeoJsonNodeProps;
      if (
        selectedOsaWay?.lifeCycleStage === LifeCycleStage.Coding ||
        selectedOsaWay?.lifeCycleStage === LifeCycleStage.Review
      ) {
        const osaWay = osaWayStore.get(props.osaWayId);
        return !!(
          osaWay?.stats?.nActiveComments &&
          osaCommentStore.commentsByOsaWayId
            .get(props.osaWayId!)
            ?.find((c) => c.osaNodeId === props.id && !c.resolved)
        );
      }
      return false;
    };

    const modifyFeature = (feature: Feature): Feature => {
      const props = feature.properties as GeoJsonNodeProps;
      const node = osaNodeStore.get(props.id);
      if (!node) return feature;

      return {
        ...feature,
        geometry: applyPendingCoords(props.id, feature.geometry),
        properties: applyFeatureProperties(feature, node),
      };
    };

    const applyPendingCoords = (
      nodeId: string,
      geometry: Geometry,
    ): Geometry => {
      if (
        geometry.type === "Point" &&
        osaNodeStore.pendingCoordsByNodeId.size
      ) {
        const pendingCoords = osaNodeStore.pendingCoordsByNodeId.get(nodeId);

        if (pendingCoords) {
          return {
            ...geometry,
            coordinates: pendingCoords,
          };
        }
      }

      return geometry;
    };

    const applyFeatureProperties = (
      feature: Feature,
      node: OsaNode,
    ): GeoJsonNodeProps => {
      const props = feature.properties as GeoJsonNodeProps;
      const osaWaySelected = selectedOsaWay?.id === node.osaWayId;
      return {
        ...props,
        nodeSelected: selectedNode?.id === node.id,
        nodeMultiSelected: osaNodeStore.multiSelectedIds.has(node.id),
        photoMissing: !node.photo,
        color: getColor(node),
        osaWaySelected,
        transparent: !!selectedOsaWay && !osaWaySelected,
        showCommentIcon: getShowCommentIcon(feature),
      };
    };

    const shouldShowPlannedTrajectory = (osaWay: OsaWay) => {
      if (selectedOsaWay?.lifeCycleStage === LifeCycleStage.Design) {
        return osaWay.plannedTrajectory && osaWay.id !== selectedOsaWay?.id;
      }
      return osaWay.plannedTrajectory && !osaWay?.geoJson;
    };

    filterStore.filteredOsaWays.forEach((osaWay) => {
      if (filterStore.isOsaWayVisible(osaWay)) {
        if (osaWay?.geoJson) {
          geoJsonFeatures.push(...osaWay.geoJson.features.map(modifyFeature));
        }

        if (shouldShowPlannedTrajectory(osaWay)) {
          plannedTrajectoryFeatures.push(
            ...osaWay.plannedTrajectory!.features.map((feature) => ({
              ...feature,
              properties: {
                osaWayId: osaWay.id,
              },
            })),
          );
        }
      }
    });

    const featuresToCollection = (features: Feature[]): FeatureCollection => ({
      type: "FeatureCollection",
      features: features,
    });

    return [
      featuresToCollection(geoJsonFeatures),
      featuresToCollection(plannedTrajectoryFeatures),
    ];

    // eslint-disable-next-line
  }, [
    filterStore.filteredOsaWays,
    filterStore.filteredNodes,
    mapStore.colorBy,
    selectedOsaWay,
    selectedNode,
    osaNodeStore.multiSelectedIds,
    osaNodeStore.pendingCoordsByNodeId,
    osaCommentStore.commentsByOsaWayId,
  ]);

  /**
   * Instantiate map and add the common controls
   */
  useEffect(() => {
    mapStore.init();

    return () => {
      mapStore.destroy();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapActive(map)) {
      mapStore.initSources(isHome);
    }

    // eslint-disable-next-line
  }, [mapStore.map, isHome]);

  useEffect(() => {
    if (mapActive(map)) {
      mapStore.onSelectedOsaWayChange(selectedOsaWay);
    }

    // eslint-disable-next-line
  }, [mapStore.map, isHome, selectedOsaWay]);

  useEffect(() => {
    if (mapActive(map)) map!.resize();
    // eslint-disable-next-line
  }, [mapStore.map, mapStore.mapResizeTrigger]);

  /**
   * When the geoJson content changes, update the map
   */
  useEffect(() => {
    if (mapActive(map)) {
      mapStore.osaMapSource?.update(processedGeoJson);
      mapStore.plannedTrajectoryMapSource?.update(processedPlannedTrajectory);

      if (isHome) {
        mapStore.projectsMapSource?.update({
          type: "FeatureCollection",
          features: projectStore.projects.map((project) => ({
            id: project.id,
            type: "Feature",
            properties: {
              id: project.id,
              name: project.name,
            },
            geometry: {
              type: "Point",
              coordinates: [project.longitude, project.latitude],
            },
          })),
        });
      }
    }
    //eslint-disable-next-line
  }, [
    map,
    processedGeoJson,
    processedPlannedTrajectory,
    projectStore.projects,
    selectedProject,
  ]);

  /**
   * Toggle route matching lines
   */
  useEffect(() => {
    if (mapActive(map)) {
      if (selectedOsaWay && osaNodeStore.pendingCoordsByNodeId.size) {
        const nodes = osaNodeStore.nodesByWayId.get(selectedOsaWay.id) ?? [];
        mapStore.pendingCoordChangesMapSource?.update(
          osaNodeStore.pendingCoordsByNodeId,
          nodes,
        );
      } else {
        mapStore.pendingCoordChangesMapSource?.reset();
      }
    }

    // eslint-disable-next-line
  }, [osaNodeStore.pendingCoordsByNodeId]);

  /**
   * Stop route matching when it's no longer active
   */
  useEffect(() => {
    if (osaNodeStore.pendingCoordsByNodeId.size) {
      osaNodeStore.clearPendingCoordChanges();
    }
    // eslint-disable-next-line
  }, [selectedOsaWay]);

  /**
   * When the list of ways is updated or way/node selection changes, re-center the map
   */
  useEffect(() => {
    if (mapActive(map)) {
      if (!selectedProject) {
        mapStore.centerHomeMap();
      }
      mapUtils.centerMap(
        map!,
        (bounds) => {
          mapStore.hlaMapSource?.extendBounds(bounds);
          mapStore.osaMapSource?.extendBounds(bounds, processedGeoJson, {
            selectedOsaWay: selectedOsaWay,
            selectedOsaNode: selectedNode,
            selectedHlaWay: selectedHlaWay,
          });
          mapStore.plannedTrajectoryMapSource?.extendBounds(
            bounds,
            mapStore.newPlannedTrajectory || processedPlannedTrajectory,
            {
              selectedWay: selectedOsaWay,
              selectedNode: selectedNode,
            },
          );

          if (bounds.isEmpty() && selectedProject) {
            map?.flyTo({
              center: [selectedProject.longitude, selectedProject.latitude],
              zoom: 12,
            });
          }
        },
        {
          selectedOsaWayId: selectedOsaWay?.id,
          selectedNodeId: selectedNode?.id,
          reducedMapSize: reducedMapSize,
        },
      );
    }
    //eslint-disable-next-line
  }, [map, mapStore.mapRecenterTrigger]);

  useEffect(() => {
    if (mapActive(map) && onMapReady) {
      onMapReady(map!);
    }
  }, [map, onMapReady]);

  return <Box id="map" flexGrow={1} height="100%" />;
};

export const MainMap = observer(MainMapBase);
